import { useCallback, useEffect, useMemo, useState } from 'react'
import uniqBy from 'lodash/uniqBy'
import { ShopifyProductWithBundles } from '@/services/Shopify/ShopifyService'
import { useTranslate } from '@/utils/translate/translate-client'
import { getQuantityMultiplier } from '../utils/quantityMultipliers'
import { useGiveawaySchedule } from './useGiveawaySchedule'

export const useGiveawayProductController = (tickets: ShopifyProductWithBundles[], theatricalSlug: string) => {
  const [selectedProduct, setSelectedProduct] = useState<ShopifyProductWithBundles | null>(null)

  const { t } = useTranslate('tickets')
  const { normal, dailyBonusEntries, minRewardTicketQuantity } = useGiveawaySchedule(theatricalSlug)
  const multiplier = normal?.multiplier ?? 1

  useEffect(() => {
    const ticketPack =
      tickets?.find((offer) => {
        return offer?.bundledProducts?.some((product) => product.quantity === 30)
      }) ??
      tickets[2] ??
      tickets[1] ??
      tickets[0]

    if (ticketPack) setSelectedProduct(ticketPack)
  }, [tickets])

  const selectedTicketsQty = useMemo(
    () =>
      selectedProduct?.bundledProducts?.find((product) => product?.product?.title?.toLowerCase()?.includes('ticket'))
        ?.quantity ?? 0,
    [selectedProduct?.bundledProducts],
  )

  const handleProductChange = useCallback(
    (offerId: string) => {
      const offerToSelect = tickets.find((product) => product?.id === offerId) as ShopifyProductWithBundles
      if (offerToSelect) setSelectedProduct(offerToSelect)
    },
    [tickets],
  )

  const getEntriesByQuantity = useCallback(
    (quantity: number) => {
      const quantityMultiplier = getQuantityMultiplier(quantity)
      const bonusDailyEntries = quantity >= minRewardTicketQuantity ? dailyBonusEntries : 0

      if (quantityMultiplier > 1) return quantity * quantityMultiplier + bonusDailyEntries

      return quantity * multiplier + bonusDailyEntries
    },
    [dailyBonusEntries, minRewardTicketQuantity, multiplier],
  )

  const allPerks = useMemo(() => {
    const perks = tickets
      ?.flatMap((product) => product?.bundledProducts)
      ?.map((bundle) => bundle?.product)
      ?.filter((product) => product?.title?.toLowerCase()?.includes('ticket'))

    const dynamicPerks = [
      {
        id: 'dynamic-tickets',
        title: t('homesteadMovieTicketsCount', '{{qty}} Homestead Movie Tickets', { qty: selectedTicketsQty }),
      },
      {
        id: 'dynamic-entries',
        title: t('giveawayEntriesCount', '{{qty}} Giveaway Entries', {
          qty: getEntriesByQuantity(selectedTicketsQty),
        }),
      },
    ]

    return [...dynamicPerks, ...uniqBy(perks, 'id')]
  }, [tickets, t, selectedTicketsQty, getEntriesByQuantity])

  const activePerkIds = useMemo(() => {
    return new Set([
      ...(allPerks ?? [])
        .filter((perk) => selectedProduct?.bundledProducts?.some((product) => product?.product?.id === perk?.id))
        .map((perk) => perk?.id),
    ])
  }, [allPerks, selectedProduct?.bundledProducts])

  const checkIsPerkActive = useCallback(
    (perkId: string) => {
      if (perkId === 'dynamic-tickets' || perkId === 'dynamic-entries') {
        return true
      }
      return activePerkIds.has(perkId)
    },
    [activePerkIds],
  )

  const ticketQuantityList = useMemo(() => {
    return tickets
      ?.map((offer) => {
        const quantity =
          offer?.bundledProducts?.find((bundle) => bundle?.product?.title?.toLowerCase()?.includes('ticket'))
            ?.quantity ?? 0
        return {
          id: offer.id,
          quantity,
          entries: getEntriesByQuantity(quantity),
        }
      })
      ?.toSorted((a, b) => a.quantity - b.quantity)
  }, [tickets, getEntriesByQuantity])

  return useMemo(() => {
    return {
      selectedProduct,
      selectedTicketsQty,
      totalSelectedEntries: getEntriesByQuantity(selectedTicketsQty),
      handleProductChange,
      ticketQuantityList,
      checkIsPerkActive,
    }
  }, [
    selectedProduct,
    selectedTicketsQty,
    getEntriesByQuantity,
    handleProductChange,
    ticketQuantityList,
    checkIsPerkActive,
  ])
}
